import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface PublicConfig {
  ssoEnabled: boolean;
  ssoFrontendUrl: string;
  /** Whether the backend has the Easyship feature (and its routes) switched on. */
  easyshipEnabled: boolean;
  /** Whether this lab is connected to the integration service. */
  integrationEnabled: boolean;
  /**
   * The part this lab plays in Dormo Direct: `source` sends patients to the
   * Direct lab, `direct` is the Direct lab, null for every other lab.
   */
  dormotechDirectRole: 'source' | 'direct' | null;
  /**
   * The countries the street fields' address suggestions come from, as ISO
   * alpha-2 codes (the backend's GOOGLE_PLACES_COUNTRIES). Empty: no suggestions.
   */
  placesCountries: string[];
}

const DEFAULT_CONFIG: PublicConfig = {
  ssoEnabled: false,
  ssoFrontendUrl: '',
  easyshipEnabled: false,
  // Off unless the backend says so: the Integration API surface is only
  // offered where integration-be can use what it configures.
  integrationEnabled: false,
  dormotechDirectRole: null,
  placesCountries: [],
};

// Bounded so a slow / hung backend can't wedge app boot. If the fetch times
// out we fall through to the safe default (SSO off) — the interceptor's 401
// path will still kick the user out cleanly if the backend really is down.
const LOAD_TIMEOUT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private config: PublicConfig = { ...DEFAULT_CONFIG };
  private loaded = false;

  constructor(private http: HttpClient) {}

  async load(): Promise<void> {
    try {
      this.config = await this.fetchConfig();
    } catch (err) {
      // Safer default: assume SSO is off. If backend is truly down the app
      // is unusable anyway, but at least the local login page renders.
      console.warn(
        '[AppConfigService] Failed to load /api/public-config, defaulting to SSO disabled',
        err,
      );
      this.config = { ...DEFAULT_CONFIG };
    } finally {
      this.loaded = true;
    }
  }

  /**
   * Re-read the config for a page that may have been open across a
   * server-side SSO switch. The cache-buster matters: `/public-config` is
   * served with a 60s cache lifetime, so a plain re-request could hand back
   * the very value that went stale. A failed re-read keeps what we had.
   */
  async refresh(): Promise<PublicConfig> {
    try {
      this.config = await this.fetchConfig({ _: String(Date.now()) });
    } catch (err) {
      console.warn('[AppConfigService] Failed to re-read /api/public-config', err);
    }
    return { ...this.config };
  }

  private async fetchConfig(params?: Record<string, string>): Promise<PublicConfig> {
    // `skip-auth` tells AuthInterceptor to pass this request through
    // without adding a bearer token or engaging the 401-refresh branch —
    // essential because this call runs before we know if there's a session.
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'skip-auth': 'true',
      'ngsw-bypass': 'true',
    });
    const res = await firstValueFrom(
      this.http
        .get<Partial<PublicConfig>>(`${environment.apiUrl}public-config`, {
          headers,
          params,
        })
        .pipe(timeout(LOAD_TIMEOUT_MS)),
    );
    return {
      ssoEnabled: !!res?.ssoEnabled,
      ssoFrontendUrl: (res?.ssoFrontendUrl ?? '').replace(/\/$/, ''),
      easyshipEnabled: !!res?.easyshipEnabled,
      integrationEnabled: res?.integrationEnabled === true,
      dormotechDirectRole:
        res?.dormotechDirectRole === 'source' || res?.dormotechDirectRole === 'direct'
          ? res.dormotechDirectRole
          : null,
      placesCountries: Array.isArray(res?.placesCountries)
        ? res.placesCountries.filter((code): code is string => typeof code === 'string' && /^[A-Z]{2}$/.test(code))
        : [],
    };
  }

  isSsoEnabled(): boolean {
    return this.config.ssoEnabled;
  }

  getSsoFrontendUrl(): string {
    return this.config.ssoFrontendUrl;
  }

  /**
   * Whether Easyship is on for this deployment. Off — the default, and what a
   * failed read falls back to — the backend does not serve the Easyship routes,
   * so every Easyship screen stays hidden.
   */
  isEasyshipEnabled(): boolean {
    return this.config.easyshipEnabled;
  }

  /**
   * Whether this lab is connected to the integration service, so the
   * department page offers its Integration API credentials and lab webhooks.
   */
  isIntegrationEnabled(): boolean {
    return this.config.integrationEnabled;
  }

  /**
   * Whether this lab sends patients to the Dormo Direct lab, so it offers Direct
   * patients and the department switch for them.
   */
  isDirectSourceLab(): boolean {
    return this.config.dormotechDirectRole === 'source';
  }

  /** Whether this is the Dormo Direct lab itself, which keeps the Labs list. */
  isDirectLab(): boolean {
    return this.config.dormotechDirectRole === 'direct';
  }

  /** The countries address suggestions come from; none means the street fields are plain inputs. */
  placesCountries(): string[] {
    return [...this.config.placesCountries];
  }

  isLoaded(): boolean {
    return this.loaded;
  }
}
